import React, { useMemo } from 'react'
import { Canvas } from '@react-three/fiber'
import { OrbitControls, Line, Html } from '@react-three/drei'
import * as THREE from 'three'

interface Props { title?: string; description?: string; onComplete?: () => void }

const L = 1.3 // nửa khoảng cách cực

function FieldLines({ density }: { density: number }) {
  const lines = useMemo(() => {
    const bulges = [0.45, 0.95, 1.6, 2.4].slice(0, density)
    const rots = [0, Math.PI / 3, (2 * Math.PI) / 3]
    const out: [number, number, number][][] = []
    for (const a of bulges) for (const rot of rots) {
      const pts: [number, number, number][] = []
      for (let i = 0; i <= 64; i++) {
        const t = (i / 64) * Math.PI * 2
        const x = L * Math.cos(t)
        const rp = a * Math.sin(t)
        pts.push([x, rp * Math.cos(rot), rp * Math.sin(rot)])
      }
      out.push(pts)
    }
    return out
  }, [density])
  return (
    <>
      {lines.map((pts, i) => (
        <Line key={i} points={pts} color="#64748b" lineWidth={1.5} transparent opacity={0.7} />
      ))}
    </>
  )
}

function Scene({ density }: { density: number }) {
  return (
    <>
      <ambientLight intensity={0.85} />
      <directionalLight position={[4, 6, 5]} intensity={0.8} />
      {/* Thanh nam châm: cực N (đỏ, +x), cực S (xanh, −x) */}
      <mesh position={[L / 2, 0, 0]}><boxGeometry args={[L, 0.5, 0.5]} /><meshStandardMaterial color="#ef4444" /></mesh>
      <mesh position={[-L / 2, 0, 0]}><boxGeometry args={[L, 0.5, 0.5]} /><meshStandardMaterial color="#3b82f6" /></mesh>
      <Html position={[L + 0.15, 0, 0]} center><span style={{ color: '#b91c1c', fontWeight: 800 }}>N</span></Html>
      <Html position={[-L - 0.15, 0, 0]} center><span style={{ color: '#1d4ed8', fontWeight: 800 }}>S</span></Html>
      <FieldLines density={density} />
      <OrbitControls enablePan={false} />
    </>
  )
}

// Từ trường (Lớp 12 — Bài 12): đường sức từ của thanh nam châm đi ra cực Bắc (N), vòng về cực Nam (S).
// Mô hình 3D xoay được; tăng mật độ để thấy rõ dạng trường lưỡng cực trong không gian.
const MagneticFieldSimulation: React.FC<Props> = ({
  title = 'Từ trường của nam châm (3D)',
  description = 'Đường sức từ là những đường cong khép kín, đi ra từ cực Bắc và vòng về cực Nam. Kéo chuột để xoay.',
  onComplete,
}) => {
  const [density, setDensity] = React.useState(3)
  const firstRef = React.useRef(false)
  const touch = () => { if (!firstRef.current) { firstRef.current = true; onComplete?.() } }
  return (
    <div className="rounded-2xl border border-rose-200 bg-white p-4 my-3">
      <h4 className="font-bold text-rose-800">{title}</h4>
      <p className="text-sm text-gray-500 mb-3">{description}</p>
      <div className="rounded-lg overflow-hidden border border-gray-100" style={{ height: 320, background: 'radial-gradient(circle at 50% 40%, #ffffff, #eef2f7)' }}>
        <Canvas camera={{ position: [3.5, 2.5, 4.5], fov: 50 }} onCreated={touch}>
          <Scene density={density} />
        </Canvas>
      </div>
      <label className="flex items-center gap-2 text-sm mt-3"><span className="w-32 text-gray-600">Mật độ đường sức</span>
        <input type="range" min={1} max={4} step={1} value={density} onChange={(e) => { touch(); setDensity(Number(e.target.value)) }} className="flex-1" />
        <span className="w-8 text-right font-semibold">{density}</span></label>
    </div>
  )
}
export default MagneticFieldSimulation
